/**
 * Utility functions for processing rating breakdown data from the backend
 */

interface RatingDistributionItem {
  range: '9-10' | '7-9' | '5-7' | '3-5' | '1-3'
  count: number
  percentage: number
}

interface RatingBreakdownData {
  staffRating?: number
  facilitiesRating?: number
  valueRating?: number
  locationRating?: number
  accommodationRating?: number
  recommendPercentage?: number
  ratingDistribution?: Array<RatingDistributionItem>
}

/**
 * Maps rating distribution range to display label
 */
const RATING_LABELS: Record<string, string> = {
  '9-10': 'Exceptional',
  '7-9': 'Good',
  '5-7': 'Okay',
  '3-5': 'Poor',
  '1-3': 'Terrible',
}

/**
 * Formats rating range for display
 */
function formatRatingRange(range: string): string {
  if (range === '9-10') return '9.0+'
  if (range === '7-9') return '7.0-9.0'
  if (range === '5-7') return '5.0-7.0'
  if (range === '3-5') return '3.0-5.0'
  if (range === '1-3') return '1.0-3.0'
  return range
}

/**
 * Converts backend ratingDistribution to frontend format
 */
export function mapRatingDistribution(
  ratingDistribution?: Array<RatingDistributionItem>,
): Array<{ id: number; label: string; range: string; progress: number; count: number }> {
  if (!ratingDistribution || ratingDistribution.length === 0) {
    return []
  }

  // Sort by range order: 9-10, 7-9, 5-7, 3-5, 1-3
  const rangeOrder = ['9-10', '7-9', '5-7', '3-5', '1-3'] as const
  const sorted = [...ratingDistribution].sort((a, b) => {
    return rangeOrder.indexOf(a.range) - rangeOrder.indexOf(b.range)
  })

  return sorted.map((item, index) => ({
    id: index + 1,
    label: RATING_LABELS[item.range] || item.range,
    range: formatRatingRange(item.range),
    progress: item.percentage,
    count: item.count,
  }))
}

/**
 * Converts backend rating breakdown to frontend format
 */
export function mapRatingBreakdown(
  breakdown?: RatingBreakdownData,
): Array<{ id: number; score: number; label: string }> {
  if (!breakdown) {
    return []
  }

  const items: Array<{ id: number; score: number; label: string }> = []

  if (breakdown.valueRating !== undefined) {
    items.push({ id: 1, score: breakdown.valueRating, label: 'Value' })
  }
  if (breakdown.facilitiesRating !== undefined) {
    items.push({ id: 2, score: breakdown.facilitiesRating, label: 'Facilities' })
  }
  if (breakdown.staffRating !== undefined) {
    items.push({ id: 3, score: breakdown.staffRating, label: 'Staff' })
  }
  if (breakdown.locationRating !== undefined) {
    items.push({ id: 4, score: breakdown.locationRating, label: 'Location' })
  }
  // Note: accommodationRating is intentionally excluded from display

  return items
}

/**
 * Gets recommend percentage text
 * @param recommendPercentage The recommend percentage (0-100)
 * @param entityType The type of entity: 'hotel', 'tour', or 'experience'
 */
export function getRecommendPercentageText(
  recommendPercentage?: number,
  entityType: 'hotel' | 'tour' | 'experience' = 'hotel',
): string | undefined {
  if (recommendPercentage === undefined || recommendPercentage === null) {
    return undefined
  }

  const entityLabel = entityType === 'tour' ? 'this tour' :
    entityType === 'experience' ? 'this experience' :
      'this hotel'

  return `${recommendPercentage}% recommend ${entityLabel}`
}
